import errorHandlers from '@/services/error-handlers';
import logger from '@/services/logger';
import paymentGateway from '@/services/payment-gateway';
import { shouldShowMessage, formatInAppMessages, filterInAppMessages } from '@/helpers/in-app-messages';
import http from '@/services/http';

export default {
  namespaced: true,

  state: {
    allowLoginByPhoneNumber: false,
    appSettings: {},
    defaultPaymentGateway: {
      methods: {},
      providers: {}
    },
    defaultStoreId: null,
    friendlyUriName: null,
    giftCardConfiguration: null,
    id: null,
    inAppMessagesByType: {
      Menu: [],
      Receipt: [],
      Loyalty: []
    },
    internalId: null,
    isLoading: true,
    name: null,
    options: {
      loyaltyOptions: {},
      skinOptions: {}
    },
    primarySignUpType: null,
    receiptInstructions: [],
    thirdPartySignIn: {
      enabled: false,
      facebook: false,
      google: false
    },
    realTimeDbConfig: null,
    payAtTable2FA: {
      enabled: false,
      dollarTriggerAmount: null,
      triggerBeforeViewTicket: null
    },
    deliveryService: null
  },

  getters: {
    enrollmentOptions(state) {
      const { hasSignUpOffer, loyaltyEnabled } = state.options.loyaltyOptions;
      const { offersEnabled } = state.options.offersOptions;
      const marketingOptIn = hasSignUpOffer || loyaltyEnabled || offersEnabled;

      return {
        marketingOptIn
      };
    },

    loyaltyOptions(state, getters, rootState, rootGetters) {
      const loyalty = rootGetters['offers/loyalty'];
      const session = rootGetters['session/session'];
      const { loyaltyEnabled, payAtTableEnabled, publicEnrollmentEnabled, payAtTableAuthenticationType, ...rest } = state.options.loyaltyOptions;
      const loyaltyEnrollmentEnabled = loyaltyEnabled && (publicEnrollmentEnabled || session.route.enrollInLoyalty);

      return {
        loyaltyEnrollmentEnabled,
        loyaltyEnabled,
        isEnabled: loyaltyEnabled,
        isVisible: loyaltyEnabled && (loyaltyEnrollmentEnabled || loyalty.isEnrolled),
        payAtTable: {
          isVisible: loyaltyEnabled && payAtTableEnabled && (loyaltyEnrollmentEnabled || loyalty.isEnrolled),
          payAtTableAuthenticationType
        },
        ...rest
      };
    },

    merchant(state, getters, rootState, rootGetters) {
      const selectedLocation = rootGetters['locations/selectedLocation'];

      const computed = {
        enabledApps: [],
        features: {},
        linkGroupsByType: {},
        userSignUpFields: {}
      };

      if (state.options.usersValidations) {
        state.options.usersValidations.forEach((validation) => {
          computed.userSignUpFields[validation.property] = validation;
        });
      }

      if (state.appSettings.resources) {
        state.appSettings.resources.forEach((resource) => {
          if (resource.value && resource.value !== 'true' && resource.value !== 'false') {
            computed.features[resource.key] = resource.value;
          }

          else {
            computed.features[resource.key] = resource.value === 'true';
          }
        });
      }

      if (selectedLocation.storeId) {
        selectedLocation.attributes.forEach((attribute) => {
          const shouldOverride = ['order-ahead', 'order-at-table', 'pay-at-table', 'read-only-menu'].includes(attribute.code);

          if (shouldOverride) {
            computed.features[attribute.code] = computed.features[attribute.code] && attribute.value;
          }
        });
      }

      if (computed.features['order-ahead']) {
        computed.enabledApps.push('order-ahead');
      }
      if (computed.features['order-at-table']) {
        computed.enabledApps.push('order-at-table');
      }
      if (computed.features['pay-at-table']) {
        computed.enabledApps.push('pay-at-table');
      }
      if (computed.features['read-only-menu']) {
        computed.enabledApps.push('read-only-menu');
      }

      if (state.options.links) {
        state.options.links.forEach((group) => {
          if (!computed.linkGroupsByType[group.linkType]) {
            computed.linkGroupsByType[group.linkType] = [];
          }

          computed.linkGroupsByType[group.linkType].push(group);
        });
      }

      return {
        ...state,
        ...computed
      };
    },

    systemStatus(state) {
      const content = {};
      const maintenanceIsActive = state.appSettings.systemStatusCode === 'OfflineForMaintenance';
      const maintenanceInfo = state.appSettings.maintenanceInfo || [];

      maintenanceInfo.forEach((info) => {
        content[info.maintenanceCode] = info;
      });

      return {
        content,
        maintenanceIsActive
      };
    },

    offerRedemptionFlow(state) {
      return state.options?.offersOptions?.inStoreRedemptionOptions?.offerRedemptionFlow;
    },

    shouldUseOfferKeyToken(state, getters) {
      return getters.offerRedemptionFlow === 'ActivateOfferKeyForSingleScan';
    },

    shouldGenerateRedemptionCode(state, getters) {
      return getters.offerRedemptionFlow === 'GenerateRedemptionCodeForSingleScan'
        || getters.offerRedemptionFlow === 'GenerateCheckinTokenForDisplay';
    },

    unlockedRewardInStoreRedemptionFlow(state) {
      return state.options?.loyaltyOptions?.pointConversionOptions?.unlockedRewardInStoreRedemptionFlow;
    }
  },

  actions: {
    async initialize({ commit, dispatch }) {
      try {
        if (!window.merchant) throw new Error('Merchant data failed to load');

        commit('SET_MERCHANT', window.merchant);

        await dispatch('loadInAppMessages');
        await dispatch('loadDefaultPaymentGateway');
      }

      catch (error) {
        logger.error(error);
        window._error = true;
      }

      finally {
        commit('SET_MERCHANT_IS_LOADING', false);
      }
    },

    async loadDefaultPaymentGateway({ commit, state }) {
      try {
        if (!state.defaultStoreId) {
          return;
        }

        const gateway = await paymentGateway.load({ storeId: state.defaultStoreId });
        commit('SET_DEFAULT_PAYMENT_GATEWAY', gateway);
      }

      catch (error) {
        errorHandlers.silent(error);
      }
    },

    async loadInAppMessages({ commit }) {
      try {
        const { data } = await http.get('/cardfree-api/v1/merchant/in-app-message');

        const inAppMessages = formatInAppMessages(data);

        commit('SET_IN_APP_MESSAGES', inAppMessages);
      }
      catch (error) {
        errorHandlers.silent(error);
      }
    },

    async markMessageSeen({ commit, state }, message) {
      try {
        /**
         * the `deviceId` request header included on all network requests
         * is used to identify the device that has seen this message
         */
        await http.put(`/cardfree-api/v1/in-app-message/${message.publicId}`);

        const filteredInAppMessages = filterInAppMessages(state.inAppMessagesByType, message);

        commit('SET_IN_APP_MESSAGES', filteredInAppMessages);
      }
      catch (error) {
        errorHandlers.silent(error);
      }
    },

    openInAppMessage({ dispatch, state }, { type, publicId = null }) {
      const message = publicId
        ? state.inAppMessagesByType[type].find(messageTypes => messageTypes.publicId === publicId)
        : state.inAppMessagesByType[type][0];

      if (message && shouldShowMessage(message)) {
        dispatch('modals/openModal', {
          name: 'inAppMessage',
          data: message,
          isRequired: !!message.isMandatory
        }, { root: true });
      }
    },

    async getMerchantFaqs({ getters }) {
      const requestUrl = getters.merchant.features['disable-ordering']
        ? '/cardfree-api/v1/merchant/faq/General'
        : '/cardfree-api/v1/merchant/faq/OrderAhead';

      function parseCategories(faqContent) {
        const categoriesObject = {};

        faqContent.faqContentModels.forEach((c) => {
          categoriesObject[c.description] = categoriesObject[c.description] || [];

          categoriesObject[c.description].push({
            answer: c.answer,
            question: c.question
          });
        });

        const categoryKeys = Object.keys(categoriesObject);
        const categories = [];

        categoryKeys.forEach((key) => {
          categories.push({
            name: key,
            items: categoriesObject[key]
          });
        });

        return categories;
      }

      try {
        const response = await http.get(requestUrl);

        const faqContent = response.data[0];

        return {
          title: faqContent.headerTitle,
          faqCategories: parseCategories(faqContent)
        };
      }
      catch (error) {
        errorHandlers.generic(error);

        return {
          title: null,
          faqCategories: null
        };
      }
    }
  },

  mutations: {
    SET_DEFAULT_PAYMENT_GATEWAY(state, gateway) {
      state.defaultPaymentGateway = gateway;
    },

    SET_IN_APP_MESSAGES(state, messages) {
      state.inAppMessagesByType = messages;
    },

    SET_MERCHANT(state, merchant) {
      state.allowLoginByPhoneNumber = !!merchant.allowLoginByPhoneNumber;
      state.appSettings = merchant.appSettings || {};
      state.defaultStoreId = merchant.defaultStoreId;
      state.friendlyUriName = merchant.friendlyUriName;
      state.giftCardConfiguration = merchant.giftCardConfiguration;
      state.id = merchant.merchantId;
      state.internalId = merchant.internalId;
      state.name = merchant.name;
      state.options = merchant.options || {};
      state.primarySignUpType = merchant.primarySignUpType;
      state.receiptInstructions = merchant.receiptInstructions || [];

      const signInTypes = merchant.supportedThirdPartyLoginTypes || [];
      state.thirdPartySignIn.facebook = signInTypes.includes('Facebook');
      state.thirdPartySignIn.google = signInTypes.includes('Google');
      state.thirdPartySignIn.enabled = state.thirdPartySignIn.facebook || state.thirdPartySignIn.google;
      state.realTimeDbConfig = merchant.realtimeMessagingDatabaseConfiguration;

      /** Pay-At-Table 2FA requirements */
      state.payAtTable2FA.enabled = merchant.payAtTable2FAEnabled;
      state.payAtTable2FA.dollarTriggerAmount = merchant.payAtTable2FADollarTriggerAmount;
      state.payAtTable2FA.triggerBeforeViewTicket = merchant.triggerPayAtTable2FAonTicket;

      // eslint-disable-next-line prefer-destructuring
      state.deliveryService = merchant.deliveryServices?.length ? merchant.deliveryServices[0].type : null;
    },

    SET_MERCHANT_IS_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    }
  }
};
